import { useEffect, useRef, useState } from "react";
import styles from "./ProdutoItem.module.scss";
import { Ellipsis, Pencil, Trash, Eye, Package } from "lucide-react";
import { IProduto } from "shared/interfaces/IProduto";
import { useNavigate } from "react-router-dom";
import { excluirProduto } from "shared/services/api/produto.api";

interface ProdutoItemProps {
  produto: IProduto;
  onDelete?: (id: string) => void;
}

export default function ProdutoItem({
  produto,
  onDelete,
}: ProdutoItemProps) {
  const [menuAberto, setMenuAberto] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const STATUS = {
    APPROVED: {
      label: "Aprovado",
      color: "#16A34A",
    },
    PENDING_APPROVAL: {
      label: "Pendente",
      color: "#F59E0B",
    },
    REJECTED: {
      label: "Rejeitado",
      color: "#DC2626",
    },
    DRAFT: {
      label: "Rascunho",
      color: "#6B7280",
    },
    ARCHIVED: {
      label: "Arquivado",
      color: "#68564eff",
    },
  } as const;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setMenuAberto(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function handleExcluir() {
    try {
      await excluirProduto(produto.id);
      onDelete?.(produto.id);
    } catch (error) {
      console.error("Erro ao excluir produto:", error);
      alert("Erro ao excluir produto");
    } finally {
      setMenuAberto(false);
    }
  }

  const estoque = Number(produto.estoque ?? 0);

  const estoqueStatus =
    estoque <= 0
      ? {
          label: "Sem estoque",
          className: styles.estoqueZerado,
        }
      : estoque <= 5
      ? {
          label: "Estoque baixo",
          className: styles.estoqueBaixo,
        }
      : {
          label: "Em estoque",
          className: styles.estoqueDisponivel,
        };

  return (
    <div className={styles.produtoItem}>
      {/* ================= ESQUERDA ================= */}

      <div className={styles.containerProduto}>
        <img
          src={
            produto.gallery?.[0] ||
            "/imagens/produto-sem-imagem.png"
          }
          alt={produto.name}
          className={styles.imgProduto}
        />

        <div className={styles.containerInfo}>
          <span className={styles.sku}>{produto.sku}</span>

          <span className={styles.nome}>{produto.name}</span>

          {/* ================= ESTOQUE ================= */}

          <div
            className={`${styles.estoque} ${estoqueStatus.className}`}
          >
            <Package size={14} />

            <span>
              {estoqueStatus.label}
              {estoque > 0 && ` · ${estoque} ${estoque === 1 ? "unidade" : "unidades"}`}
            </span>
          </div>
        </div>
      </div>

      {/* ================= DIREITA ================= */}

      <div className={styles.rightSide}>
        <div className={styles.menuWrapper} ref={menuRef}>
          <button
            className={styles.menuBtn}
            onClick={() =>
              setMenuAberto((prev) => !prev)
            }
          >
            <Ellipsis size={18} />
          </button>

          {menuAberto && (
            <div className={styles.menuPopup}>
              <button
                onClick={() =>
                  navigate(`/produto/${produto.id}`)
                }
              >
                <Eye size={14} />
                Visualizar
              </button>

              <button
                onClick={() =>
                  navigate(`/editarproduto/${produto.id}`)
                }
              >
                <Pencil size={14} />
                Editar
              </button>

              <button
                className={styles.danger}
                onClick={handleExcluir}
              >
                <Trash size={14} />
                Excluir
              </button>
            </div>
          )}
        </div>

        <div className={styles.status}>
          <span
            style={{
              color:
                STATUS[
                  produto.status as keyof typeof STATUS
                ]?.color ?? "#6B7280",
            }}
          >
            {STATUS[
              produto.status as keyof typeof STATUS
            ]?.label ?? produto.status}
          </span>
        </div>
      </div>
    </div>
  );
}